import React from 'react';
import { EnhancementMetadata, CrosslinkMetadata } from './types';
import './EnhancedLogDisplay.scss';

export interface EnhancedLogDisplayProps {
  /** Original duty log text */
  originalText: string;
  /** Enhanced text with bracketed expansions */
  enhancedText: string;
  /** Crosslinking metadata for clickable elements */
  metadata: EnhancementMetadata;
  /** Callback when a crosslink is clicked */
  onCrosslinkClick?: (metadata: CrosslinkMetadata) => void;
  /** Display mode: 'original', 'enhanced', or 'split' */
  displayMode?: 'original' | 'enhanced' | 'split';
}

export interface CrosslinkSpan {
  text: string;
  metadata?: CrosslinkMetadata;
  isClickable: boolean;
}

/**
 * Enhanced Log Display Component
 * 
 * Renders duty log text with interactive crosslinks for fire codes, crew IDs, and aircraft.
 * Supports multiple display modes and click handlers for contextual navigation.
 */
const EnhancedLogDisplay: React.FC<EnhancedLogDisplayProps> = ({
  originalText,
  enhancedText,
  metadata,
  onCrosslinkClick,
  displayMode = 'enhanced'
}) => {

  /**
   * Parse enhanced text into spans with crosslink metadata
   */
  const parseEnhancedText = (): CrosslinkSpan[] => {
    const spans: CrosslinkSpan[] = [];
    let text = enhancedText;
    let currentPos = 0;

    // Collect ONLY actual crosslinks (fire codes, crews, aircraft) - NOT general enhancements
    const allCrosslinks: CrosslinkMetadata[] = [
      ...metadata.fire_codes,
      ...metadata.crews,
      ...metadata.aircraft
      // DO NOT include metadata.general - those should not be clickable
    ].sort((a, b) => a.position - b.position);

    // Process each crosslink
    for (const crosslink of allCrosslinks) {
      // Find the enhanced term in the enhanced text
      const enhancedTermIndex = text.indexOf(crosslink.enhanced, currentPos);

      if (enhancedTermIndex >= 0) {
        // Add text before the crosslink as non-clickable span
        if (enhancedTermIndex > currentPos) {
          spans.push({
            text: text.substring(currentPos, enhancedTermIndex),
            isClickable: false
          });
        }

        // Add the crosslink as clickable span
        spans.push({
          text: crosslink.enhanced,
          metadata: crosslink,
          isClickable: true
        });

        // Move past this crosslink
        currentPos = enhancedTermIndex + crosslink.enhanced.length;
      }
    }

    // Add remaining text as non-clickable span
    if (currentPos < text.length) {
      spans.push({
        text: text.substring(currentPos),
        isClickable: false
      });
    }

    return spans;
  };

  /**
   * Handle crosslink click
   */
  const handleCrosslinkClick = (metadata: CrosslinkMetadata) => {
    if (onCrosslinkClick) {
      onCrosslinkClick(metadata);
    }
  };

  /**
   * Get CSS class for crosslink type
   */
  const getCrosslinkClass = (type?: string): string => {
    switch (type) {
      case 'fire_code':
        return 'crosslink-fire-code';
      case 'crew_id':
        return 'crosslink-crew';
      case 'aircraft_id':
        return 'crosslink-aircraft';
      default:
        return 'crosslink-general';
    }
  };

  /**
   * Render enhanced text with crosslinks
   */
  const renderEnhancedText = () => {
    const spans = parseEnhancedText();

    return (
      <div className="enhanced-log-text">
        {spans.map((span, index) => (
          span.isClickable && span.metadata ? (
            <span
              key={index}
              className={`crosslink ${getCrosslinkClass(span.metadata.type)}`}
              onClick={() => {
                console.debug('[EnhancedLogDisplay] Crosslink clicked', span.metadata);
                handleCrosslinkClick(span.metadata!);
              }}
              title={`Click to search for: ${span.metadata.searchTerm || span.metadata.enhanced}`}
            >
              {span.text}
            </span>
          ) : (
            <span key={index} className="regular-text">
              {span.text}
            </span>
          )
        ))}
      </div>
    );
  };

  /**
   * Render original text (no crosslinks)
   */
  const renderOriginalText = () => (
    <div className="original-log-text">
      {originalText}
    </div>
  );

  /**
   * Render split view (original | enhanced)
   */
  const renderSplitView = () => (
    <div className="split-view">
      <div className="split-original">
        <h4>Original</h4>
        {renderOriginalText()}
      </div>
      <div className="split-enhanced">
        <h4>Enhanced</h4>
        {renderEnhancedText()}
      </div>
    </div>
  );

  /**
   * Render crosslink statistics
   */
  const renderStats = () => {
    const totalCrosslinks = metadata.fire_codes.length +
      metadata.crews.length +
      metadata.aircraft.length +
      metadata.general.length;

    if (totalCrosslinks === 0) return null;

    return (
      <div className="crosslink-stats">
        <span className="stats-label">Crosslinks: </span>
        {metadata.fire_codes.length > 0 && (
          <span className="stat-item fire-codes">
            {metadata.fire_codes.length} fire{metadata.fire_codes.length === 1 ? '' : 's'}
          </span>
        )}
        {metadata.crews.length > 0 && (
          <span className="stat-item crews">
            {metadata.crews.length} crew{metadata.crews.length === 1 ? '' : 's'}
          </span>
        )}
        {metadata.aircraft.length > 0 && (
          <span className="stat-item aircraft">
            {metadata.aircraft.length} aircraft
          </span>
        )}
        {metadata.general.length > 0 && (
          <span className="stat-item general">
            {metadata.general.length} non-crosslink{metadata.general.length === 1 ? '' : 's'}
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="enhanced-log-display">
      {displayMode === 'original' && renderOriginalText()}
      {displayMode === 'enhanced' && renderEnhancedText()}
      {displayMode === 'split' && renderSplitView()}
      {displayMode !== 'original' && renderStats()}
    </div>
  );
};

export default EnhancedLogDisplay;
